/**
 * Growth stages and note shortcuts: the gardener's own words and colours,
 * and whether Grow moves their plants' stages for them.
 *
 * Every change here is a tap — a colour, a new order, hiding, adding, the
 * switch — or a short name kept on Enter, so the screen has no Save. Each
 * list says "Saved" beside its heading when a change is kept, and deleting
 * asks first.
 *
 * "Move my plants' stages for me" (2026-10-05) is the one setting for Grow's
 * automatic stages (backend services/grow/stageEstimates), on by default:
 * off, every plant shows only the stages recorded for it.
 *
 * A switch that fails to save on our side (a 5xx or no answer) says the
 * standard sentence, reported to Sentry, with the support address as a link
 * (D31); other failures keep their own words.
 *
 * Used by: UserProfile (?s=labels).
 *
 * @module LabelsScreen
 */

import React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from '@/shared/hooks/useTranslation';
import { useUserConfig, type Preferences } from '@/shared/context/userConfigStore';
import { Switch } from '@/shared/components/ui/switch';
import { PLANTS_QUERY_KEY } from '@/config/tunables/ui';
import { PLANT_OUTLOOK_QUERY_KEY } from '@/config/tunables/shared';
import { ProfileScreen } from './ProfileScreen';
import { SettingList, SettingRow } from './profileKit';
import { useSavedTicks } from './useSavedTicks';
import { StageSettings } from './StageSettings';
import { NoteShortcutSettings } from './NoteShortcutSettings';
import { errorMessage } from '@/shared/lib/errorMessage';
import { FailureText } from '@/shared/components/ServerFailure';

/**
 * "Move my plants' stages for me": kept the moment it is switched, with
 * "Saved" beside it. The garden is read again, since every plant's stage may
 * change with it, and Today's lines that go by stage with it.
 */
const AutoStagesSetting: React.FC = () => {
  const { reactTranslate: t } = useTranslation();
  const { userConfig, savePreferences } = useUserConfig();
  const queryClient = useQueryClient();
  const { saved, flash } = useSavedTicks();
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const id = React.useId();
  const on = userConfig.preferences.autoStages;

  /**
   * Keeps the choice, then reads the garden again.
   *
   * @param next - On or off.
   */
  const keep = async (next: boolean): Promise<void> => {
    setError(null);
    setSaving(true);
    try {
      await savePreferences({ ...userConfig.preferences, autoStages: next } as Preferences);
      flash('autoStages');
      void queryClient.invalidateQueries({ queryKey: [PLANTS_QUERY_KEY] });
      void queryClient.invalidateQueries({ queryKey: [PLANT_OUTLOOK_QUERY_KEY] });
    } catch (failure) {
      // A failure on our side is the standard sentence, reported (D31).
      setError(errorMessage(failure, t('profile.preferences.toast.saveFailed')));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-2">
      <SettingList>
        <SettingRow title={t('profile.labels.autoStages.title')} hint={t('profile.labels.autoStages.hint')} saved={saved('autoStages')} htmlFor={id}>
          <Switch id={id} checked={on} disabled={saving} onCheckedChange={(next) => void keep(next)} />
        </SettingRow>
      </SettingList>
      {error && <p className="text-sm text-destructive"><FailureText text={error} /></p>}
    </div>
  );
};

/** The profile's Labels screen: the automatic stages switch, stages, then note shortcuts. */
export const LabelsScreen: React.FC<{ open: boolean; onClose: () => void }> = ({ open, onClose }) => {
  const { reactTranslate: t } = useTranslation();
  return (
    <ProfileScreen open={open} title={t('profile.index.labels')} onClose={onClose}>
      <p className="text-sm text-muted-foreground">{t('profile.labels.description')}</p>
      <AutoStagesSetting />
      <div className="space-y-8 rounded-lg border bg-card p-4">
        <StageSettings />
        <NoteShortcutSettings />
      </div>
    </ProfileScreen>
  );
};
